import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { differenceInDays, format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Check,
  X,
  MessageCircle,
} from "lucide-react";
import {
  getLeaves,
  applyLeave,
  cancelLeave,
  getLeaveBalance,
  updateLeaveStatus,
  getMyLeaves
} from "../api/leaves";
import {
  getLeaveSettings,
  getLeaveBalance as getStructuredLeaveBalance,
  type LeaveSettings as LeaveSettingsData,
  type LeaveBalance as StructuredBalance,
  type LeaveType,
} from "../api/leaveSettings";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Leave } from "../types";

type ApplyErrorState = {
  open: boolean;
  message: string;
  balance_remaining?: number;
  leave_type?: string;
};

export function Leaves() {
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [allLeaves, setAllLeaves] = useState<Leave[]>([]);
  const [leaveBalance, setLeaveBalance] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approved" | "rejected">(
    "approved"
  );
  const [approvalComments, setApprovalComments] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [newLeave, setNewLeave] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",


  });
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const { user } = useAuth();
  const { toast } = useToast();
  const [managerID, setManagerID] = useState<string | null>(null);
  const [employeeID, setEmployeeID] = useState<string | null>(null);
  const [epmtID, setEpmtID] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [leaveSettings, setLeaveSettings] = useState<LeaveSettingsData | null>(null);
  const [structuredBalance, setStructuredBalance] = useState<StructuredBalance | null>(null);
  const [applyError, setApplyError] = useState<ApplyErrorState>({ open: false, message: "" });


  const isManager =
    user?.role === "manager" || user?.role === "hr head" || user?.role === "admin" || user?.role === "supervisor" || user?.role === "director" || user?.role === "founder";



  const initLeavesPage = async () => {
    try {
      setLoading(true);

      let context: any = {};

      if (
        user.role === "employee" ||
        user.role === "supervisor" ||
        user.role === "manager" ||
        user.role === "hr head" ||
        user.role === "director" ||
        user.role === "founder"
      ) {
        context = await getLeaveBalance();

        setLeaveBalance(context.balance || {});
        setManagerID(context.managerID);
        setEmployeeID(context.employeeID);
        setEpmtID(context.epmtID);
      }

      try {
        const role = user?.role;
        const [settings, balance] = await Promise.all([
          getLeaveSettings(),
          role === "admin" ? Promise.resolve(null) : getStructuredLeaveBalance(),
        ]);
        setLeaveSettings(settings);
        if (balance) setStructuredBalance(balance);
      } catch (e) {
        console.warn("leave settings/balance fetch failed", e);
      }

      let query = "";

      if (user.role === "admin") {
        query = "";
      } else {
        query = context.managerID;
      }

      console.log("ROLE:", user.role);
      console.log("QUERY SENT TO getLeaves:", query);

      const response = await getLeaves(query);

      if (isManager) {
        setAllLeaves(response.leaves);
        setLeaves(
          response.leaves.filter(
            (leave) => leave.employeeId === user?._id
          )
        );
      } else {
        setLeaves(response.leaves);
      }

    } catch (err) {
      console.error("initLeavesPage error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initLeavesPage();
  }, []);


  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await getLeaves("");


      if (isManager) {
        // Managers see all leaves
        setAllLeaves(response.leaves);
        // Also set user's own leaves for the "My Leaves" tab
        setLeaves(
          response.leaves.filter((leave) => leave.employeeId === user?._id)
        );
      } else {
        // Employees see only their own leaves
        setLeaves(response.leaves);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLeaves = async () => {
    try {
      setLoading(true);
      const res = await getMyLeaves();
      setLeaves(res.Leaves);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch my leaves",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //     fetchMyLeaves();
  //   }, []);
  useEffect(() => {
    if (activeTab === "my") {
      fetchMyLeaves();
    }
  }, [activeTab]);



  const fetchLeaveBalance = async () => {
    try {
      if (user?._id) {
        const response = await getLeaveBalance(user._id);
        setLeaveBalance(response.balance);
      }
    } catch (error) {
      console.error("Error fetching leave balance:", error);
    }
  };

  const handleApplyLeave = async () => {
    try {
      if (!startDate || !endDate) {
        toast({
          title: "Error",
          description: "Please select start and end dates",
          variant: "destructive",
        });
        return;
      }

      const leaveData = {
        employeeID: employeeID,
        managerID: managerID || "",
        epmtID: epmtID,
        name: user.name,
        fromDate: format(startDate, "yyyy-MM-dd"),
        toDate: format(endDate, "yyyy-MM-dd"),
        totalDays: differenceInDays(endDate, startDate) + 1,
        leaveType: newLeave.leaveType,
        reason: newLeave.reason,
        status: "PENDING",
        appliedOn: new Date().toISOString(),
      };

      await applyLeave(leaveData);

      toast({
        title: "Success",
        description: "Leave request submitted successfully",
      });

      setIsApplyDialogOpen(false);
      setNewLeave({ leaveType: "", startDate: "", endDate: "", reason: "" });
      setStartDate(undefined);
      setEndDate(undefined);

      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setApplyError({
          open: true,
          message: detail.detail || detail.message || "Leave request blocked",
          balance_remaining: detail.balance_remaining,
          leave_type: detail.leave_type,
        });
        return;
      }
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to submit leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleCancelLeave = async (leaveId: string) => {
    try {
      await cancelLeave(leaveId);
      toast({
        title: "Success",
        description: "Leave request withdrawn successfully",
      });
      initLeavesPage();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string" ? detail : error?.message || "Failed to withdraw leave";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleApproveReject = (
    leave: Leave,
    action: "approved" | "rejected"
  ) => {
    setSelectedLeave(leave);
    setApprovalAction(action);
    setApprovalComments("");
    setIsApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      if (!selectedLeave) return;

      await updateLeaveStatus(
        selectedLeave._id,
        approvalAction,
        approvalComments
      );

      toast({
        title: "Success",
        description: `Leave request ${approvalAction} successfully`,
      });

      setIsApprovalDialogOpen(false);
      setSelectedLeave(null);
      setApprovalComments("");
      fetchLeaves();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const filteredLeaves = leaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    return statusMatch && typeMatch;
  });

  const filteredAllLeaves = allLeaves.filter((leave) => {
    const statusMatch = filterStatus === "all" || leave.status === filterStatus;
    const typeMatch = filterType === "all" || leave.leaveType === filterType;
    return statusMatch && typeMatch;
  });

  const pendingLeaves = allLeaves.filter((leave) => leave.status === "pending");

  const renderLeaveCard = (leave: Leave, showApprovalButtons = false) => (
    <Card
      key={leave._id}
      className="bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(leave.status)}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {leave.leaveType.replace("_", " ")} Leave
                </h3>
                {showApprovalButtons && (
                  <p className="text-sm text-gray-600">
                    by {leave.employeeName}
                  </p>
                )}
              </div>
              <Badge className={cn("border", getStatusColor(leave.status))}>
                {leave.status?.charAt(0).toUpperCase() + leave.status.slice(1)}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Duration:</span>
                <br />
                {format(new Date(leave.startDate), "MMM dd, yyyy")} -{" "}
                {format(new Date(leave.endDate), "MMM dd, yyyy")}
                <div className="text-xs text-gray-500 mt-1">
                  {leave.days} days
                </div>
              </div>

              <div>
                <span className="font-medium">Applied:</span>
                <br />
                {leave.appliedDate
                  ? format(new Date(leave.appliedDate), "MMM dd, yyyy")
                  : "—"}
              </div>

              {leave.approvedBy && (
                <div>
                  <span className="font-medium">
                    Processed:
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">by {leave.approvedBy}</span>
                </div>
              )}
            </div>

            {leave.reason && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Reason:
                </span>
                <p className="text-sm text-gray-600 mt-1">{leave.reason}</p>
              </div>
            )}

            {leave.managerComments && (
              <div className="mt-3">
                <span className="font-medium text-sm text-gray-700">
                  Manager Comments:
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {leave.managerComments}
                </p>
              </div>
            )}
          </div>

          <div className="ml-4 flex flex-col gap-2">
            {showApprovalButtons && leave.status === "pending" && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleApproveReject(leave, "approved")}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApproveReject(leave, "rejected")}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}

            {!showApprovalButtons &&
              (leave.status === "pending" || leave.status === "approved") &&
              (employeeID
                ? leave.employeeId === employeeID
                : leave.employeeId === user?._id) &&
              (() => {
                if (!leave.startDate) return false;
                const start = new Date(leave.startDate);
                start.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return today < start;
              })() && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancelLeave(leave._id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Withdraw
                </Button>
              )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
      {/* Header */}
      <div className="gt-page-hero flex-col md:flex-row gap-4">
        <div>
          <div className="gt-eyebrow-row">
            <span className="gt-leaf" />
            <span className="gt-eyebrow">iv. People</span>
          </div>
          <h1 className="gt-page-title">Leave <em>Management</em></h1>
          <p className="gt-page-sub" style={{ marginTop: 6 }}>
            {isManager
              ? <>Manage leave <em className="gt-serif-italic">requests</em> and view balances.</>
              : <>Manage your leave <em className="gt-serif-italic">requests</em> and view balances.</>}
          </p>
        </div>

        {(user?.role === "employee" || user?.role === "manager" || user?.role === "supervisor" || user?.role === "hr head" || user?.role === "director" || user?.role === "founder") && (
          <Dialog open={isApplyDialogOpen} onOpenChange={setIsApplyDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gt-btn is-primary is-lg">
                <Plus className="h-4 w-4" />
                Apply for Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
                <DialogDescription>
                  Submit a new leave request for approval
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="leaveType">Leave Type</Label>
                  <Select
                    value={newLeave.leaveType}
                    onValueChange={(value) =>
                      setNewLeave({ ...newLeave, leaveType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const balanceTypes = structuredBalance
                          ? (Object.keys(structuredBalance.balances) as LeaveType[])
                          : null;
                        const fallback: LeaveType[] = ["sick", "annual", "casual"];
                        const baseTypes: LeaveType[] = balanceTypes && balanceTypes.length
                          ? balanceTypes
                          : (() => {
                              const list: LeaveType[] = [...fallback];
                              if (leaveSettings?.maternity_enabled) list.push("maternity");
                              if (leaveSettings?.paternity_enabled) list.push("paternity");
                              return list;
                            })();
                        const labels: Record<LeaveType, string> = {
                          sick: "Sick",
                          annual: "Annual",
                          casual: "Casual",
                          maternity: "Maternity",
                          paternity: "Paternity",
                        };
                        return baseTypes.map((t) => {
                          const bal = structuredBalance?.balances[t];
                          const available =
                            typeof bal?.available === "number"
                              ? bal.available
                              : bal?.remaining;
                          return (
                            <SelectItem key={t} value={t}>
                              {labels[t]}
                              {typeof available === "number"
                                ? ` (${available} available)`
                                : ""}
                            </SelectItem>
                          );
                        });
                      })()}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover open={startOpen} onOpenChange={setStartOpen}>
                      <PopoverTrigger asChild>
                        {/* <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !startDate && "text-muted-foreground"
                          )}
                        > */}
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !startDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate
                            ? format(startDate, "MMM dd, yyyy")
                            : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={(date) => {
                            setStartDate(date);
                            setStartOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover open={endOpen} onOpenChange={setEndOpen}>
                      <PopoverTrigger asChild>
                        {/* <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !endDate && "text-muted-foreground"
                          )}
                        > */}
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !endDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate
                            ? format(endDate, "MMM dd, yyyy")
                            : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={(date) => {
                            setEndDate(date);
                            setEndOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter reason for leave..."
                    value={newLeave.reason}
                    onChange={(e) =>
                      setNewLeave({ ...newLeave, reason: e.target.value })
                    }
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsApplyDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleApplyLeave}>Submit Application</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Leave Balance Cards */}
      {structuredBalance && Object.keys(structuredBalance.balances).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(Object.entries(structuredBalance.balances) as [LeaveType, any][]).map(
            ([type, entry]) => (
              <div key={type} className="gt-stat">
                <div className="gt-stat-label" style={{ textTransform: "capitalize" }}>
                  {type}
                </div>
                <p className="gt-stat-value">{entry.remaining}</p>
                <p className="gt-page-sub" style={{ fontSize: 12, marginTop: 2 }}>
                  of {entry.allocated} days
                  {entry.reserved_pending > 0
                    ? ` · ${entry.reserved_pending} pending`
                    : ""}
                </p>
              </div>
            )
          )}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={isApprovalDialogOpen}
        onOpenChange={setIsApprovalDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {approvalAction === "approved" ? "Approve" : "Reject"} Leave
              Request
            </DialogTitle>
            <DialogDescription>
              {selectedLeave && (
                <>
                  {approvalAction === "approved" ? "Approve" : "Reject"} leave
                  request from {selectedLeave.employeeName} for{" "}
                  {selectedLeave.leaveType} leave from{" "}
                  {format(new Date(selectedLeave.startDate), "MMM dd, yyyy")} to{" "}
                  {format(new Date(selectedLeave.endDate), "MMM dd, yyyy")}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="comments">
                Comments{" "}
                {approvalAction === "rejected" && (
                  <span className="text-red-500"></span>
                )}
              </Label>
              <Textarea
                id="comments"
                placeholder={
                  approvalAction === "approved"
                    ? "Add any comments (optional)..."
                    : "Please provide a reason for rejection (optional)..."
                }
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                className="min-h-20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsApprovalDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprovalSubmit}
              // disabled={
              //   approvalAction === "rejected" && !approvalComments.trim()
              // }
              className={
                approvalAction === "approved"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
            >
              {approvalAction === "approved" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      {isManager ? (
        // <Tabs defaultValue="pending" className="space-y-6">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value)}
          className="space-y-6"
        >
          <TabsList
            className={`grid w-full ${user?.role === "admin" ? "grid-cols-2" : "grid-cols-3"
              }`}
          >
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending Approvals ({pendingLeaves.length})
            </TabsTrigger>

            <TabsTrigger value="all">All Team Requests</TabsTrigger>

            {user?.role !== "admin" && (
              <TabsTrigger value="my">My Leaves</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                  Pending Leave Requests
                </CardTitle>
                <CardDescription>
                  Review and approve or reject leave requests from your team
                  members
                </CardDescription>
              </CardHeader>
            </Card>

            {pendingLeaves.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No pending requests
                  </h3>
                  <p className="text-gray-600 text-center">
                    All leave requests have been processed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {pendingLeaves.map((leave) => renderLeaveCard(leave, true))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-4">
            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="vacation">Vacation</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="maternity">Maternity</SelectItem>
                        <SelectItem value="paternity">Paternity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                All Team Leave Requests
              </h2>

              {filteredAllLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      No leave requests match your current filters.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredAllLeaves.map((leave) =>
                    renderLeaveCard(leave, leave.status === "pending")
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="my" className="space-y-4">
            {/* Leave Balance Cards for managers */}
            {user?.role === "manager" && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(leaveBalance).map(([type, balance]) => (
                  <Card
                    key={type}
                    className="bg-white/80 backdrop-blur-sm border-0 shadow-lg"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-600 capitalize">
                        {type.replace("_", " ")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-gray-900">
                        {balance}
                      </div>
                      <p className="text-xs text-gray-500">days remaining</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Filters */}
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-blue-600" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={filterStatus}
                      onValueChange={setFilterStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="vacation">Vacation</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="maternity">Maternity</SelectItem>
                        <SelectItem value="paternity">Paternity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                My Leave Requests
              </h2>

              {filteredLeaves.length === 0 ? (
                <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No leave requests found
                    </h3>
                    <p className="text-gray-600 text-center">
                      {leaves.length === 0
                        ? "You haven't submitted any leave requests yet."
                        : "No leave requests match your current filters."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filteredLeaves.map((leave) => renderLeaveCard(leave, false))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        // Employee view (existing functionality)
        <>
          {/* Filters */}
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-blue-600" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Leave Type</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="vacation">Vacation</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="maternity">Maternity</SelectItem>
                      <SelectItem value="paternity">Paternity</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Leave Requests List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Your Leave Requests
            </h2>

            {filteredLeaves.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No leave requests found
                  </h3>
                  <p className="text-gray-600 text-center">
                    {leaves.length === 0
                      ? "You haven't submitted any leave requests yet."
                      : "No leave requests match your current filters."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredLeaves.map((leave) => renderLeaveCard(leave, false))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Apply error / insufficient-balance popup */}
      <Dialog
        open={applyError.open}
        onOpenChange={(open) => setApplyError((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cannot submit leave</DialogTitle>
            <DialogDescription>{applyError.message}</DialogDescription>
          </DialogHeader>
          {typeof applyError.balance_remaining === "number" && (
            <div className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <strong>{applyError.balance_remaining}</strong>{" "}
              day{applyError.balance_remaining === 1 ? "" : "s"} of{" "}
              {applyError.leave_type || "this type"} leave remaining.
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setApplyError({ open: false, message: "" })}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
